import { useCallback, useEffect, useState } from 'react'
import {
  fetchLegalDocumentsAdmin,
  publishLegalDocument,
} from '@/lib/api/legal-documents-admin.service'
import { useCampaignTotpStepUp } from '@/contexts/CampaignTotpStepUpContext'
import { ApiClientError } from '@/lib/api/client'
import { defaultLegalDocumentVersion } from '@/lib/legal-document-version'
import type {
  LegalDocumentAdminItem,
  LegalDocumentType,
  LegalDocumentsAdminListResponse,
} from '@/types/legal-documents'

export function useLegalDocumentAdmin(documentType: LegalDocumentType) {
  const { runWithStepUp } = useCampaignTotpStepUp()
  const [data, setData] = useState<LegalDocumentsAdminListResponse | null>(null)
  const [version, setVersion] = useState(() => defaultLegalDocumentVersion())
  const [content, setContent] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isPublishing, setIsPublishing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const load = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await runWithStepUp(() => fetchLegalDocumentsAdmin(documentType))
      setData(response)
      const current = response.current
      setVersion(defaultLegalDocumentVersion())
      setContent(current?.content ?? '')
    } catch (err) {
      setData(null)
      setError(
        err instanceof ApiClientError
          ? err.message
          : 'Não foi possível carregar os documentos.'
      )
    } finally {
      setIsLoading(false)
    }
  }, [documentType, runWithStepUp])

  useEffect(() => {
    void load()
  }, [load])

  const selectVersion = useCallback((item: LegalDocumentAdminItem) => {
    setVersion(item.version)
    setContent(item.content)
    setSuccessMessage(null)
    setError(null)
  }, [])

  const publish = useCallback(async () => {
    const trimmedVersion = version.trim()
    const trimmedContent = content.trim()
    if (!trimmedVersion || trimmedContent.length < 20) {
      setError('Informe a versão e um conteúdo válido (mínimo 20 caracteres).')
      return
    }

    setIsPublishing(true)
    setError(null)
    setSuccessMessage(null)
    try {
      await runWithStepUp(() =>
        publishLegalDocument({
          document_type: documentType,
          version: trimmedVersion,
          content: trimmedContent,
        })
      )
      setSuccessMessage(
        'Nova versão publicada. Instituições e colaboradores precisarão aceitar novamente.'
      )
      await load()
    } catch (err) {
      if (err instanceof ApiClientError && err.errors) {
        const firstKey = Object.keys(err.errors)[0]
        const firstMessage = firstKey ? err.errors[firstKey]?.[0] : undefined
        setError(firstMessage ?? err.message)
      } else {
        setError(
          err instanceof ApiClientError
            ? err.message
            : 'Não foi possível publicar a nova versão.'
        )
      }
    } finally {
      setIsPublishing(false)
    }
  }, [content, documentType, load, version, runWithStepUp])

  return {
    data,
    version,
    setVersion,
    content,
    setContent,
    isLoading,
    isPublishing,
    error,
    successMessage,
    load,
    selectVersion,
    publish,
  }
}
